import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { useGlowPointer } from "@/motion/useGlowPointer";

export interface GlowCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Depth via glow spread, not grey shadows. */
  elevation?: 1 | 2 | 3;
  /** Rotating conic edge light (featured cards). */
  featured?: boolean;
  /** Halo follows the pointer and brightens on hover. Default true. */
  interactive?: boolean;
  padded?: boolean;
  /** Classes for the outer light wrapper (grid placement, margins). `className` styles the surface. */
  wrapperClassName?: string;
}

/**
 * Surface panel lit from behind: the halo lives on the outer wrapper, the opaque surface sits on
 * top of it, so light only spills around the edges.
 * <GlowCard elevation={2} featured>…</GlowCard>
 */
export const GlowCard = forwardRef<HTMLDivElement, GlowCardProps>(function GlowCard(
  { elevation = 1, featured = false, interactive = true, padded = true, className, wrapperClassName, onPointerMove, children, ...rest },
  ref,
) {
  const glow = useGlowPointer();
  return (
    <div
      ref={ref}
      onPointerMove={(e) => {
        if (interactive) glow(e);
        onPointerMove?.(e);
      }}
      className={cn("relative", interactive && "backlit", wrapperClassName)}
      {...rest}
    >
      <div
        className={cn(
          "relative h-full rounded-card border border-fg/[0.06] bg-surface",
          featured && "edge-light",
          { 1: "shadow-glow-1", 2: "shadow-glow-2", 3: "shadow-glow-3" }[elevation],
          padded && "p-5 sm:p-6",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
});
